<template>
  <v-container fluid class="pa-7">
    <template v-if="hasOrderInfoLoaded && getOrder.order && getOrder.order.order_state_id === ORDER_STATE.CHECKING">
      <div class="d-flex align-center flex-wrap gap-4 mb-4">
        <h2>
          {{ getOrder.order.id }}
        </h2>

        <span v-if="getOrder.search_user">
          (celkem {{ getOrder.items.length }} produktů / {{ totalAmount }} kusů)
          Vyhledal/a: {{ getOrder.search_user.first_name }} {{ getOrder.search_user.last_name }}
        </span>

        <v-chip v-if="isFirstTimeCustomer" class="ml-auto font-weight-bold" color="success" size="large" label>
          {{ $vuetify.lang.t('$vuetify.labels.newCustomer').toUpperCase() }}
        </v-chip>
      </div>
      <div class="d-flex gap-4">
        <h4>Poznámka zákazníka:</h4>
        <p>
          {{ getOrder.order.customer_note }}
        </p>
      </div>

      <div class="d-flex align-baseline justify-space-between">
        <v-switch v-model="hideCheckedProducts" :label="$vuetify.lang.t(`$vuetify.check.hideSwitch`)"></v-switch>

        <div class="d-flex align-baseline gap-4">
          <!--  UNDO LAST CHECK BUTTON  -->
          <v-tooltip bottom>
            <template #activator="{ on }">
              <v-btn v-on="on" color="error" :disabled="!areSomeItemsChecked" @click="uncheckLastProduct" outlined>
                {{ $vuetify.lang.t('$vuetify.labels.uncheckLastCheckedProduct') }}
              </v-btn>
            </template>

            <span>
              {{ $vuetify.lang.t('$vuetify.labels.escape') }}
            </span>
          </v-tooltip>
          <!--  end of undo last check button  -->

          <v-select
            :items="[
              {
                name: $vuetify.lang.t(`$vuetify.global.ascending`),
                value: SortDirection.ASCENDING,
              },
              {
                name: $vuetify.lang.t(`$vuetify.global.descending`),
                value: SortDirection.DESCENDING,
              },
            ]"
            item-text="name"
            item-value="value"
            :label="$vuetify.lang.t(`$vuetify.global.sorting`)"
            dense
            outlined
            hide-details
            class="flex-grow-0"
            v-model="sortedBy"
          />
        </div>
      </div>

      <v-card v-if="getOrder.items" class="searched_products">
        <v-card-text class="pa-0">
          <ProductToCheck
            v-for="item in sortedAllItems"
            :key="item.item.id"
            :itemData="item"
            :modified-as="(checkedItems[item.item.id] && checkedItems[item.item.id].modification_reason) || null"
            @check-product="checkProduct"
            @uncheck-product="uncheckProduct"
            :hide-checked-products="hideCheckedProducts"
          />
        </v-card-text>
      </v-card>

      <v-text-field
        :label="$vuetify.lang.t(`$vuetify.check.note`)"
        outlined
        hide-details
        v-model="note"
        clearable
        class="my-7"
      />

      <v-footer fixed class="py-3 px-0 pa-lg-5 primary lighten-5">
        <div class="d-flex justify-space-between w-100">
          <v-btn @click="_redirectToList" class="mx-3" color="grey">
            {{ $vuetify.lang.t(`$vuetify.searching.close`) }}
          </v-btn>

          <v-btn
            @click="preSaveAllItems"
            class="mx-3"
            color="primary"
            :loading="isSaveLoading"
            :disabled="!isCheckingComplete || isSaveLoading"
          >
            {{ $vuetify.lang.t(`$vuetify.searching.save`) }}
          </v-btn>
        </div>

        <v-dialog v-model="notAllFound" width="500">
          <v-card>
            <v-card-title class="text-h5 amber mb-5">
              <v-icon color="white" class="mb-1 mr-3"> $vuetify.icons.triangleExclamation </v-icon>
            </v-card-title>

            <v-card-text>
              {{ $vuetify.lang.t(`$vuetify.check.miss1`) }}
              <span class="font-weight-bold">
                <span v-for="(item, index) in errorItems" :key="index">
                  {{ item.sku }}<span v-html="index < errorItems.length - 1 ? ',' : ''"></span>
                </span>
              </span>
              {{ $vuetify.lang.t(`$vuetify.check.miss2`) }}
            </v-card-text>

            <v-divider />

            <v-card-actions>
              <v-spacer />
              <v-btn color="primary" text @click="saveAllItems" :loading="isSaveLoading" :disabled="isSaveLoading">
                {{ $vuetify.lang.t(`$vuetify.check.continue`) }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-footer>
    </template>

    <v-progress-circular
      v-else-if="!hasOrderInfoLoaded"
      indeterminate
      color="primary"
      :size="50"
      :width="6"
      class="d-block mx-auto mt-5"
    />

    <p v-else class="color-red mt-2">
      {{ $vuetify.lang.t(`$vuetify.check.badStatus`) }}
    </p>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { orderBy } from 'lodash';

import ProductToCheck from '../../components/check/ProductToCheck.vue';
import ORDER_STATE from '@/common/enums/orderState';
import { SortDirection } from '@/common/enums/sorting';
import ModificationReason from '../../common/enums/modificationReason';
import { firstTimeCustomerMixin } from '@/mixins/firstTimeCustomer';
import { isFirstTimeCustomer } from '@/common/helpers/customer';

export default {
  name: 'CheckDetail',

  mixins: [firstTimeCustomerMixin],

  components: {
    ProductToCheck,
  },

  data() {
    return {
      ORDER_STATE,
      SortDirection,
      hideCheckedProducts: localStorage.getItem('CheckSwitchStatus')
        ? JSON.parse(localStorage.getItem('CheckSwitchStatus'))
        : true,
      sortedBy: null,
      /**
       * A map of the checked items.
       * The key is the product id so that the access is O(1).
       * @type {{
       *   [productId: number]: {
       *     id: number,
       *     modification_reason: string,
       *     modification_reason_count: number,
       *   }
       * }}
       */
      checkedItems: {},
      lastCheckedItemIndex: null,
      note: '',
      loadingTimeout: 0,
      hasOrderInfoLoaded: false,
      notAllFound: false,
      errorItems: [],
      isSaveLoading: false,
    };
  },

  watch: {
    hideCheckedProducts: function () {
      localStorage.setItem('CheckSwitchStatus', this.hideCheckedProducts);
    },
  },

  computed: {
    ...mapGetters({
      getOrder: 'stores/getOrder',
    }),

    sortedAllItems() {
      const startWithALetter = [];
      const startWithANumber = [];

      for (const item of this.getOrder.items) {
        // push the item to the correct array based on the first character of the sku
        if (item.item.sku.match(/^[a-zA-Z]/)) {
          startWithALetter.push(item);
        } else {
          startWithANumber.push(item);
        }
      }

      // return an array where we sort the items the SKU of which starts with a number &
      // add the items the SKU of which starts with a letter to the end of the array
      return [...orderBy(startWithANumber, 'item.sku', this.sortedBy), ...startWithALetter];
    },

    areSomeItemsChecked() {
      return Object.values(this.checkedItems).length > 0;
    },

    isCheckingComplete() {
      return Object.values(this.checkedItems).length === this.getOrder.items.length;
    },

    totalAmount() {
      return this.getOrder.items.reduce((sum, item) => sum + item.item.amount, 0);
    },
  },

  methods: {
    ...mapActions({
      fetchOrderItems: 'stores/fetchOrderItems',
      postOrderItems: 'stores/postOrderItems',
    }),

    preSaveAllItems() {
      this.isSaveLoading = true;

      this.errorItems = Object.values(this.checkedItems).filter(item => item.modification_reason !== 'checked');

      if (this.errorItems.length > 0) {
        this.notAllFound = true;

        this.isSaveLoading = false;
      } else {
        this.saveAllItems();
      }
    },

    async saveAllItems() {
      this.isSaveLoading = true;

      try {
        await this.postOrderItems({
          url: 'orders-quality-assurance/' + this.$router.currentRoute.params.id,
          formData: { order_items: Object.values(this.checkedItems), note: this.note },
        });

        await this.$router.push({ name: 'check', params: { id: this.$router.currentRoute.params.id } });
      } catch (error) {
        this.isSaveLoading = false;
      }
    },

    checkProduct(productId, productSku, typeOfCheck, productAmount) {
      this.$set(this.checkedItems, productId, {
        id: productId,
        sku: productSku,
        modification_reason: typeOfCheck,
        modification_reason_count: productAmount,
      });
    },

    uncheckProduct(productId) {
      this.$delete(this.checkedItems, productId);
    },

    uncheckLastProduct() {
      let lastCheckedProductIndex = null;
      for (const item of this.sortedAllItems) {
        if (this.checkedItems[item.item.id]) {
          lastCheckedProductIndex = item.item.id;
        }
      }

      if (!lastCheckedProductIndex) return;

      this.$delete(this.checkedItems, lastCheckedProductIndex);
    },

    /**
     * @param { KeyboardEvent } event
     */
    keyDownEventHandler(event) {
      switch (event.key) {
        case 'Enter':
          // skip event handling if there are no items to check
          if (!this.sortedAllItems.length || this.isCheckingComplete) return;

          // find the first non-checked item
          const productToCheck = this.sortedAllItems.find(item => !this.checkedItems[item.item.id]);
          // skip event handling if no such item was found
          if (!productToCheck) return;

          // set the first non-checked item as CHECKED
          this.checkProduct(
            productToCheck.item.id,
            productToCheck.item.sku,
            ModificationReason.CHECKED,
            productToCheck.item.amount
          );
          break;

        case 'Escape':
          // skip event handling if there are no items checked
          if (!this.areSomeItemsChecked) return;

          // uncheck the last checked item
          this.uncheckLastProduct();
          break;
      }
    },

    _redirectToList() {
      this.$router.push({ name: 'check' });
    },
  },

  async created() {
    try {
      const orderId = this.$router.currentRoute.params.id;
      const isAlreadyLoaded = this.getOrder && this.getOrder.order && String(this.getOrder.order.id) === String(orderId);

      if (!isAlreadyLoaded) {
        await this.fetchOrderItems({ url: 'orders-quality-assurance/' + orderId });
      }

      // check if the customer is a first time customer
      this.isFirstTimeCustomer = await isFirstTimeCustomer(this.getOrder && this.getOrder.order.customer_id);
      // a flag used to display the order items only after the information about the first time customer is fetched
      this.hasOrderInfoLoaded = true;
    } catch (error) {
      this.$alertError(this.$vuetify.lang.t('$vuetify.messages.ordersQualityAssuranceFetchError'));
    }
  },

  mounted() {
    window.addEventListener('keydown', this.keyDownEventHandler);
  },

  beforeDestroy() {
    window.removeEventListener('keydown', this.keyDownEventHandler);

    // reset the background color when navigating away from the page (method from a mixin)
    this.resetBackground();
  },
};
</script>
